import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

export const useDropDetail = (dropId, userId) => {
  const [drop,       setDrop]       = useState(null);
  const [tiers,      setTiers]      = useState([]);
  const [tracks,     setTracks]     = useState([]);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const fetch = useCallback(async () => {
    if (!dropId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: dropData, error: dropErr } = await supabase
        .from('d2f_drops')
        .select(`
          id, title, description, genre, mood, tags, cover_url,
          min_price, suggested_price, unlock_count, is_published, created_at,
          artist:d2f_artists(id, name, slug, avatar_url, bio, genre)
        `)
        .eq('id', dropId)
        .maybeSingle();

      if (dropErr) throw dropErr;
      if (!dropData) throw new Error('Drop not found');
      setDrop(dropData);

      const [tiersRes, tracksRes] = await Promise.all([
        supabase
          .from('d2f_subscription_tiers')
          .select('id, name, price, color, perks')
          .eq('artist_id', dropData.artist?.id)
          .order('price', { ascending: true }),
        supabase
          .from('d2f_tracks')
          .select('id, title, duration, track_number, is_free, audio_url')
          .eq('drop_id', dropId)
          .order('track_number', { ascending: true }),
      ]);

      setTiers(tiersRes.data || []);
      setTracks(tracksRes.data || []);

      if (userId) {
        const { data: unlock } = await supabase
          .from('d2f_drop_unlocks')
          .select('id')
          .eq('drop_id', dropId)
          .eq('user_id', userId)
          .maybeSingle();
        setIsUnlocked(!!unlock);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dropId, userId]);

  useEffect(() => { fetch(); }, [fetch]);

  const unlockDrop = async (amount) => {
    if (!userId) return { error: new Error('Not logged in') };
    const { error } = await supabase
      .from('d2f_drop_unlocks')
      .upsert(
        { drop_id: dropId, user_id: userId, amount_paid: amount },
        { onConflict: 'drop_id,user_id' }
      );
    if (!error) setIsUnlocked(true);
    return { error };
  };

  return { drop, tiers, tracks, isUnlocked, loading, error, unlockDrop, refetch: fetch };
};